import {
  EmrDottedRightArrow,
  EmrNotes,
  EmrRemove,
  EmrVoiceNote,
} from "@medstonetech/slate-icons";
import * as React from "react";
import {
  Box,
  Button,
  chakra,
  CSSObject,
  Flex,
  HStack,
  Icon,
  IconButton,
  useDisclosure,
  keyframes,
} from "@chakra-ui/react";
import { CardProps, Card } from "shared";
import { ChartNotesModal } from "modules";
import { SharedChartNotesModal } from "../chart-notes/SharedChartNotesModal";
import { useChartSectionNotesCount } from "modules/charts-shared/api/queries/useChartSectionNotesCount";
import { EncounterIntegrationCards } from "modules/reception/components/encounter-integration";
import {
  useAuditPermissions,
  useChartTrackingContext,
  useSpeechToTextContext,
} from "contexts";
import { normalizeSpeechCrossOptionFieldValue } from "modules/charts-shared/utils/normalizeSpeechCrossOptionFieldValue";

import { ChartFavorites } from "../chart-favorites";
import { useLocation } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import {
  AuditCommentsHeaderSectionIndicator,
  AuditCommentsHeaderSharedSectionIndicator,
} from "../audit-comments";
import { ChartWebsocket } from "../websocket";
import { useFormContext } from "react-hook-form";
import { LiveTranscript } from "../speech-to-text/LiveTranscript";
import { NURSE_CHARTS } from "modules/triage/constants";
import { PROVIDER_CHARTS } from "modules/provider/constants";

type WithNotes = {
  icon: JSX.Element;
  chartId: string;
  encounterId: string;
  sectionId: string;
  showNotesBtn?: true;
  rightLink?: JSX.Element;
  externalLink?: string;
  externalLinkLabel?: string;
};

type NoNotes = {
  icon: JSX.Element;
  showNotesBtn: false;
  encounterId?: string;
  sectionId?: string;
  chartId?: string;
  showInformationSentButtons?: boolean;
  showFavoritesBtn?: boolean;
  rightLink?: JSX.Element;
  externalLink?: string;
  externalLinkLabel?: string;
};

type ActionBtnsProps = {
  showImportBtn?: boolean;
  isImportDisabled?: boolean;
  showDeleteBtn?: boolean;
  showActionsBtns?: boolean;
  showAuditsBtns?: boolean;
  onCancel?: () => void;
  onImport?: () => void;
  onSave?: () => void;
  onSaveAndNext?: () => void;
  onDeleteSection?: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  isSticky?: boolean;
  sectionLabel?: string;
  isShared?: boolean;
  useWebsocket?: boolean;
  onReload?: () => void;
  allowResponsiveWrap?: boolean;
};

type ChartSectionHeaderProps = (WithNotes | NoNotes) &
  ActionBtnsProps &
  CardProps;

type ChartSectionHeaderCardProps = {
  icon: JSX.Element;
  showNotesBtn: boolean;
  onOpen?: () => void;
  sectionNotesCount?: number; // number of notes - turn blue icon to indicate if has notes
  sectionBgColor?: string;
  sectionMargin?: string;
  sectionPadding?: string;
  encounterId?: string;
  showInformationSentButtons?: boolean;
  wrapperStyles?: CSSObject;
  showFavoritesBtn?: boolean;
  sectionId?: string;
  chartId?: string;
  rightLink?: JSX.Element;
  externalLink?: string;
  externalLinkLabel?: string;
  extraActionComponent?: JSX.Element;
} & ActionBtnsProps &
  CardProps;

function isPropsNoNotes(props: ChartSectionHeaderProps): props is NoNotes {
  return (props as NoNotes).showNotesBtn === false;
}

const CHART_NAMES: Record<string, string> = {
  ...NURSE_CHARTS,
  ...PROVIDER_CHARTS,
};

function getCopiedFromChartName(chartCode: string | null): string | null {
  if (!chartCode) return null;
  const normalizedCode = chartCode.trim();
  if (!normalizedCode) return null;

  return (
    CHART_NAMES[normalizedCode as keyof typeof CHART_NAMES] ?? normalizedCode
  );
}

const breathing = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(8, 121, 237, 0.4); }
  70% { transform: scale(1.1); box-shadow: 0 0 10px 20px rgba(8, 121, 237, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(8, 121, 237, 0); }
`;

function ChartSectionHeaderCard(props: ChartSectionHeaderCardProps) {
  const {
    onOpen,
    showNotesBtn,
    showDeleteBtn,
    showImportBtn = false,
    isImportDisabled,
    showActionsBtns,
    showAuditsBtns = false,
    onCancel,
    onImport,
    onSave,
    onSaveAndNext,
    isLoading,
    isDisabled,
    icon,
    children,
    sectionLabel,
    sectionNotesCount,
    sectionBgColor = "gray.50",
    sectionMargin = "0rem",
    sectionPadding = "1rem 0 0 0",
    showInformationSentButtons = false,
    encounterId,
    onDeleteSection = () => {},
    wrapperStyles = {},
    isSticky = true,
    showFavoritesBtn,
    chartId,
    sectionId,
    isShared = false,
    externalLink,
    externalLinkLabel = "View",
    rightLink = null,
    extraActionComponent,
    useWebsocket,
    onReload,
    allowResponsiveWrap = false,
    ...cardProps
  } = props;

  const { isPastEncounter, isCurrentChartCompleted, currentChartElements } =
    useChartTrackingContext();
  // Commenters (audits access without edit) must still see audit comments even
  // though audited charts are chart-`Completed`, which makes
  // isCurrentChartCompleted true for them.
  const { isAuditor, isCommenter } = useAuditPermissions();

  const {
    isListening,
    isProcessing,
    startListening,
    stopListening,
    processWithLLM,
    transcript,
    parsedFields,
    cancelAndReset,
  } = useSpeechToTextContext();
  const [searchParams] = useSearchParams();

  const formContext = useFormContext();

  React.useEffect(() => {
    if (parsedFields) {
      parsedFields.forEach(({ key, value }) => {
        const fieldType = currentChartElements?.find(
          (element) => element.name === key
        )?.type;
        const normalizedValue = normalizeSpeechCrossOptionFieldValue(
          value,
          fieldType
        );
        formContext?.setValue?.(key, normalizedValue, {
          shouldDirty: true,
          shouldValidate: false,
        });
      });
      cancelAndReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedFields]);

  const handleToggleListening = () => {
    if (isListening) {
      stopListening();
      processWithLLM(transcript);
    } else {
      startListening();
    }
  };

  const location = useLocation();

  const fromAudits = location.pathname.includes("audits");
  const fromSummary = location.pathname.includes("medical-records");
  const copiedFromChartCode = searchParams.get("copiedFromChartCode");
  const copiedFromChartName = getCopiedFromChartName(copiedFromChartCode);
  const headerHeight = showInformationSentButtons ? "6rem" : "4.5rem";

  return (
    <Box
      margin={sectionMargin}
      padding={sectionPadding}
      paddingBottom="0"
      height={allowResponsiveWrap ? "auto" : headerHeight}
      marginBottom={
        showInformationSentButtons ? "3.5rem !important" : "2rem !important"
      }
      position={isSticky && !fromSummary ? "sticky" : "relative"}
      top="-1.5rem"
      zIndex={isSticky ? 10 : 9}
      background={sectionBgColor}
      sx={wrapperStyles}
    >
      {encounterId && showInformationSentButtons && (
        <Flex justify="center">
          <EncounterIntegrationCards
            encounterId={encounterId}
            showAYVAPharmacyIntegration
            isDisabled={isDisabled}
          />
        </Flex>
      )}
      <Card
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap={allowResponsiveWrap ? "wrap" : undefined}
        rowGap={allowResponsiveWrap ? "0.75rem" : undefined}
        columnGap={allowResponsiveWrap ? "1.25rem" : undefined}
        padding="1rem 1.25rem"
        variant="default"
        border="none"
        {...cardProps}
      >
        <HStack spacing="0.625rem">
          <Box
            borderRadius="5px"
            bg="blue"
            width="1.875rem"
            height="1.875rem"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            {React.cloneElement(icon, {
              color: "white",
              fontSize: "1.25rem",
            })}
          </Box>
          <chakra.h2 fontSize="1.125rem" fontWeight="700">
            {children}

            {showActionsBtns &&
              !isPastEncounter &&
              !fromSummary &&
              showImportBtn && (
                <Button
                  ml={4}
                  variant="action"
                  onClick={onImport}
                  type="submit"
                  colorScheme="blue"
                  height="2rem"
                  isLoading={isLoading}
                  isDisabled={isLoading || isDisabled || isImportDisabled}
                >
                  Import
                </Button>
              )}
          </chakra.h2>

          {extraActionComponent}

          {showFavoritesBtn && chartId && sectionId && !fromSummary && (
            <ChartFavorites chartCode={chartId} sectionCode={sectionId} />
          )}
          {encounterId &&
            showAuditsBtns &&
            fromAudits &&
            (!isCurrentChartCompleted || isCommenter) &&
            (isAuditor || isCommenter) &&
            (isShared ? (
              <AuditCommentsHeaderSharedSectionIndicator
                chartCode={chartId || ""}
                sectionCode={sectionId || ""}
                encounterId={encounterId || ""}
                isFromSideBar={false}
                isFromSubSection={false}
                sectionLabel={sectionLabel || ""}
              />
            ) : (
              <AuditCommentsHeaderSectionIndicator
                chartCode={chartId || ""}
                sectionCode={sectionId || ""}
                encounterId={encounterId || ""}
                isFromSideBar={false}
                isFromSubSection={false}
                sectionLabel={sectionLabel || ""}
              />
            ))}
        </HStack>

        {externalLink && (
          <Button
            as="a"
            href={externalLink}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlineSquared"
            colorScheme="blue"
            justifySelf="center"
            alignItems="center"
            columnGap="0.5rem"
            p="0.625rem 1.25rem"
            height="auto"
            width="auto"
          >
            <Icon as={EmrDottedRightArrow} />
            {externalLinkLabel}
          </Button>
        )}

        {rightLink &&
          (allowResponsiveWrap ? (
            <Box maxW="100%">{rightLink}</Box>
          ) : (
            React.cloneElement(rightLink, {
              style: { marginLeft: "1.75rem" },
            })
          ))}

        <HStack spacing="0.825rem">
          {copiedFromChartName && (
            <chakra.span fontSize="0.8125rem" color="gray.600" fontWeight="600">
              Copied from {copiedFromChartName}
            </chakra.span>
          )}
          <IconButton
            aria-label="Toggle speech to text"
            variant="icon"
            icon={<Icon as={EmrVoiceNote} w="24px" h="24px" />}
            onClick={handleToggleListening}
            colorScheme={isListening ? "blue" : "gray"}
            animation={
              isListening ? `${breathing} 2s ease-out infinite` : "none"
            }
            isLoading={isProcessing}
          />
          {showNotesBtn && !fromSummary && (
            <IconButton
              aria-label="notes"
              icon={
                <Icon
                  as={EmrNotes}
                  color={
                    sectionNotesCount && sectionNotesCount > 0
                      ? "blue"
                      : "gray.450"
                  }
                  fontSize="1.375rem"
                />
              }
              onClick={onOpen}
              variant="ghost"
              height="2rem"
              width="2rem"
              minWidth="unset"
            />
          )}
          {showDeleteBtn && !fromSummary && (
            <Button
              variant="ghost"
              color="red"
              fontSize="1.0625rem"
              disabled={isPastEncounter}
              rightIcon={
                <Icon
                  borderRadius="1rem"
                  color="red"
                  backgroundColor="white"
                  as={EmrRemove}
                />
              }
              onClick={onDeleteSection}
            >
              Remove
            </Button>
          )}
          {showActionsBtns && !isPastEncounter && !fromSummary && (
            <>
              <Button
                variant="action"
                onClick={onSave}
                type="submit"
                colorScheme="blue"
                height="2rem"
                isLoading={isLoading}
                isDisabled={isLoading || isDisabled}
              >
                Save
              </Button>
              {onSaveAndNext && (
                <Button
                  variant="solid"
                  onClick={onSaveAndNext}
                  colorScheme="blue"
                  height="2rem"
                  isLoading={isLoading}
                  isDisabled={isLoading || isDisabled}
                  sx={{ borderRadius: "5px" }}
                >
                  Save & Next
                </Button>
              )}
            </>
          )}
        </HStack>
      </Card>

      {useWebsocket && encounterId && chartId && sectionId && (
        <ChartWebsocket
          encounterId={encounterId}
          chartCode={chartId}
          sectionCode={sectionId}
          onReload={onReload}
          sectionName={sectionLabel ?? ""}
        />
      )}
      <LiveTranscript />
    </Box>
  );
}

function ChartSectionHeaderWithNotes(
  props: WithNotes & ActionBtnsProps & CardProps
) {
  const {
    chartId,
    encounterId,
    sectionId,
    icon,
    showNotesBtn = true,
    children,
    isDisabled,
    ...cardProps
  } = props;
  const { onOpen, onClose, isOpen } = useDisclosure();
  const { data } = useChartSectionNotesCount({
    encounterId,
    sectionCode: sectionId,
    chartCode: chartId,
  });

  const sectionNotesCount = data?.data || 0;

  return (
    <>
      <ChartSectionHeaderCard
        encounterId={encounterId}
        onOpen={onOpen}
        showNotesBtn={showNotesBtn}
        icon={icon}
        sectionNotesCount={sectionNotesCount}
        chartId={chartId}
        sectionId={sectionId}
        {...cardProps}
      >
        {children}
      </ChartSectionHeaderCard>
      {isOpen && (
        <ChartNotesModal
          chartId={chartId}
          isOpen={isOpen}
          encounterId={encounterId}
          onClose={onClose}
          sectionId={sectionId}
          title={children}
          isDisabled={isDisabled}
        />
      )}
    </>
  );
}

function ChartSectionHeader(props: ChartSectionHeaderProps) {
  if (isPropsNoNotes(props)) {
    const { encounterId, sectionId, chartId, ...rest } = props;
    return (
      <ChartSectionHeaderCard
        encounterId={encounterId}
        sectionId={sectionId}
        chartId={chartId}
        {...rest}
      />
    );
  }
  return <ChartSectionHeaderWithNotes {...props} />;
}

type SharedWithNotes = {
  icon: JSX.Element;
  encounterId: string;
  sectionId: string;
  chartCode?: string;
  showNotesBtn?: true;
  areNotesShared?: boolean;
  sectionBgColor?: string;
  sectionPadding?: string;
  showFavoritesBtn?: boolean;
  extraActionComponent?: JSX.Element;
};

type SharedChartSectionHeaderProps = (SharedWithNotes | NoNotes) &
  ActionBtnsProps &
  CardProps & { wrapperStyles?: CSSObject };

function SharedChartSectionHeaderWithNotes(
  props: SharedWithNotes & ActionBtnsProps & CardProps
) {
  const {
    encounterId,
    sectionId,
    chartCode,
    icon,
    showNotesBtn = true,
    children,
    sectionBgColor,
    sectionPadding,
    isDisabled,
    showFavoritesBtn,
    extraActionComponent,
    ...cardProps
  } = props;
  const { onOpen, onClose, isOpen } = useDisclosure();
  const { data } = useChartSectionNotesCount({
    encounterId,
    sectionCode: sectionId,
  });

  const sectionNotesCount = data?.data || 0;

  return (
    <>
      <ChartSectionHeaderCard
        onOpen={onOpen}
        isShared
        showNotesBtn={showNotesBtn}
        icon={icon}
        sectionNotesCount={sectionNotesCount}
        sectionBgColor={sectionBgColor}
        sectionPadding={sectionPadding}
        encounterId={encounterId}
        isDisabled={isDisabled}
        sectionId={sectionId}
        chartId={chartCode}
        showFavoritesBtn={showFavoritesBtn}
        extraActionComponent={extraActionComponent}
        {...cardProps}
      >
        {children}
      </ChartSectionHeaderCard>
      {isOpen && (
        <SharedChartNotesModal
          encounterId={encounterId}
          isOpen={isOpen}
          onClose={onClose}
          sectionCode={sectionId}
          title={children}
          isDisabled={isDisabled}
        />
      )}
    </>
  );
}

function isPropsSharedNoNotes(
  props: SharedChartSectionHeaderProps
): props is NoNotes {
  return (props as NoNotes).showNotesBtn === false;
}

function SharedChartSectionHeader(props: SharedChartSectionHeaderProps) {
  if (isPropsSharedNoNotes(props)) {
    const { encounterId, ...rest } = props;
    return (
      <ChartSectionHeaderCard encounterId={encounterId} isShared {...rest} />
    );
  }
  return <SharedChartSectionHeaderWithNotes {...props} />;
}

export type { ChartSectionHeaderProps, NoNotes, ActionBtnsProps };
export { ChartSectionHeader, SharedChartSectionHeader, ChartSectionHeaderCard };
